<template>
  <v-card class="filter-panel pa-4" width="300">
    <!-- Выбранных состояний бывает до шести: полным перечнем они не помещаются в поле
         шириной 300px и обрезаются на середине слова. Показываем первое и счётчик. -->
    <v-select v-model="local.state" :items="stateItems" :label="$t('custom.state')" multiple clearable
      hide-details dense prepend-inner-icon="mdi-car-cog" class="mb-4" @change="emitNow">
      <template v-slot:selection="{ item, index }">
        <span v-if="index === 0" class="text-truncate">{{ item.text }}</span>
        <span v-if="index === 1" class="grey--text text-caption ml-1">+{{ local.state.length - 1 }}</span>
      </template>
    </v-select>

    <v-select v-model="local.car_type" :items="typeItems" :label="$t('cars_board.type_label')" clearable
      hide-details dense prepend-inner-icon="mdi-shape-outline" class="mb-4" @change="emitNow" />

    <template v-if="rentalObject !== 'bicycles'">
      <v-select v-model="local.transmission" :items="gearItems" :label="$t('cars.gear_big')" clearable
        hide-details dense prepend-inner-icon="mdi-car-shift-pattern" class="mb-4" @change="emitNow" />
      <v-select v-model="local.fuel" :items="fuelItems" :label="$t('cars.fuel')" clearable
        hide-details dense prepend-inner-icon="mdi-gas-station" class="mb-4" @change="emitNow" />
    </template>

    <!-- теги — обычным мультиселектом в ряду остальных фильтров (иконка-кнопка выбивалась) -->
    <v-autocomplete v-model="local.tag_ids" :items="tagItems" :label="$t('tags.filter_by_tags')"
      multiple clearable hide-details dense prepend-inner-icon="mdi-tag-multiple-outline" class="mb-4"
      :loading="tagsLoading" @focus="loadTagsOnce" @change="emitNow">
      <template v-slot:selection="{ item }">
        <v-chip x-small :color="item.color" outlined class="mr-1">{{ item.text }}</v-chip>
      </template>
    </v-autocomplete>

    <!-- Фильтры, которых нет в общем apply_cars_filters: их принимает только
         free_cars_for_rent_v2, поэтому на страницах парка они не показываются. -->
    <v-text-field v-if="extras.indexOf('min_seats') !== -1" v-model="local.min_seats"
      :label="$t('cars.number_seats_from')" type="number" min="0" clearable hide-details dense
      prepend-inner-icon="mdi-seat-passenger" class="mb-4" @input="emitNow" @click:clear="emitNow" />
    <v-checkbox v-if="extras.indexOf('is_air') !== -1" v-model="local.is_air"
      :label="$t('cars.is_air')" hide-details dense class="mt-0 mb-2" @change="emitNow" />
    <v-checkbox v-if="extras.indexOf('no_deposit') !== -1" v-model="local.no_deposit"
      :label="$t('cars.no_deposit')" hide-details dense class="mt-0 mb-2" @change="emitNow" />

    <!-- «В аренде сейчас» и «Активные» показываются не везде: на поиске свободных
         эндпоинт эти два параметра не принимает (он и так отдаёт только активные и
         только незанятые), и элемент управления обещал бы фильтр, которого нет. -->
    <template v-if="showAvailability">
      <v-checkbox v-model="inRentProxy" :label="$t('cars_board.in_rent_now')" hide-details dense
        class="mt-0 mb-2" @change="emitNow" />
      <v-select v-model="local.active" :items="activeItems" :label="$t('custom.active')" clearable
        hide-details dense class="mb-2" @change="emitNow" />
    </template>

    <template v-if="showGroup">
      <v-divider class="my-4"></v-divider>
      <v-select v-model="local.group_by" :items="groupItems" :label="$t('cars_board.group_label')"
        hide-details dense prepend-inner-icon="mdi-view-column-outline" @change="emitNow" />
      <!-- FT-217 REQ-06: фильтр по ценовой группе — только в «Ближайших днях», варианты
           доска собирает из сводок групп своего ответа -->
      <v-select v-if="local.group_by === 'upcoming' && priceGroupItems.length" v-model="local.price_group_id"
        :items="priceGroupItems" :label="$t('price_groups.group_label')" clearable hide-details dense
        prepend-inner-icon="mdi-tag-text-outline" class="mt-4" data-testid="board-price-group-filter"
        @change="emitNow" />
    </template>

    <!-- FT-087 REQ-09: сохранённые пресеты фильтров (localStorage, как у броней) -->
    <v-divider class="my-4"></v-divider>
    <div v-if="presets.length" class="d-flex flex-wrap align-center mb-3" style="gap: 6px">
      <v-icon small class="mr-1">mdi-bookmark-outline</v-icon>
      <v-chip v-for="(p, i) in presets" :key="i" small close @click="applyPreset(p)" @click:close="deletePreset(i)">
        {{ p.name }}
      </v-chip>
    </div>
    <div class="d-flex align-center" style="gap: 8px">
      <v-text-field v-model="presetName" :label="$t('cars.name')" hide-details dense
        @keyup.enter="savePreset" />
      <v-btn small color="primary" :disabled="!presetName.trim()" @click="savePreset">
        <v-icon left small>mdi-content-save-outline</v-icon>{{ $t("custom.save") }}
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import { typeDictFor, GEARS, FUELS } from "@/components/dashboard/cars/carDictionaries";

export default {
  name: "CarsFilterPanel",
  props: {
    value: { type: Object, required: true },
    rentalObject: { type: String, default: "cars" },
    showGroup: { type: Boolean, default: false },
    // «в аренде сейчас» / «активные» — только там, где эндпоинт их принимает
    showAvailability: { type: Boolean, default: true },
    // какие состояния вообще достижимы в выдаче: поиск свободных отдаёт [1,2,4,6],
    // и предлагать «критическое»/«не выдавать» значило бы предлагать заведомо пустой
    // результат. null — все шесть.
    stateOptions: { type: Array, default: null },
    // пресеты у страниц разные по составу полей, поэтому и ключ хранилища разный
    presetsKey: { type: String, default: "cars_filter_presets" },
    // необязательные фильтры конкретной страницы: "min_seats", "is_air", "no_deposit"
    extras: { type: Array, default: () => [] },
    // FT-217: варианты фильтра ценовой группы доски [{ text, value }] (value — id | "none")
    priceGroupItems: { type: Array, default: () => [] },
  },
  data() {
    return { local: { ...this.value }, presets: [], presetName: "", tagItems: [], tagsLoading: false, tagsLoaded: false };
  },
  created() {
    this.presets = this.loadPresets();
  },
  computed: {
    stateItems() {
      const all = [
        { text: this.$t("states.normal"), value: 1 },
        { text: this.$t("states.in_service"), value: 2 },
        { text: this.$t("states.critical"), value: 3 },
        { text: this.$t("states.long_rent"), value: 4 },
        { text: this.$t("states.no_rent"), value: 5 },
        { text: this.$t("states.service_needed"), value: 6 },
      ];
      if (!this.stateOptions) return all;
      return all.filter((item) => this.stateOptions.indexOf(item.value) !== -1);
    },
    typeItems() {
      // текст — из i18n по слагу словаря текущего вида транспорта, value — кириллица БД
      const prefix = this.rentalObject === "bicycles" ? "bike_types."
        : this.rentalObject === "motorcycles" ? "motorcycle_types." : "car_types.";
      return typeDictFor(this.rentalObject).map((e) => ({ text: this.$t(prefix + e.slug), value: e.value }));
    },
    gearItems() {
      return GEARS.map((e) => ({ text: this.$t("transmissions." + e.slug), value: e.value }));
    },
    fuelItems() {
      return FUELS.map((e) => ({ text: this.$t("fuels." + e.slug), value: e.value }));
    },
    activeItems() {
      return [
        { text: this.$t("cars_board.active_only"), value: true },
        { text: this.$t("cars_board.inactive_only"), value: false },
      ];
    },
    groupItems() {
      return [
        { text: this.$t("cars_board.group_availability"), value: "availability" },
        { text: this.$t("cars_board.group_state"), value: "state" },
        { text: this.$t("cars_board.group_upcoming"), value: "upcoming" },
      ];
    },
    inRentProxy: {
      get() { return this.local.in_rent === true; },
      set(v) { this.local.in_rent = v ? true : null; },
    },
  },
  watch: {
    value(v) { this.local = { ...v }; },
  },
  methods: {
    emitNow() { this.$emit("input", { ...this.local }); },
    // тот же источник, что у TagFilter: GET /api/v1/tag_groups (JSONAPI), лениво по фокусу.
    // Заголовки групп — {header}; системные имена групп переводятся как в TagFilter.
    loadTagsOnce() {
      if (this.tagsLoaded) return;
      this.tagsLoaded = true;
      this.tagsLoading = true;
      this.axios
        .get("/api/v1/tag_groups", { headers: { Authorization: this.$store.getters.getAuthToken } })
        .then((response) => {
          const included = (response.data && response.data.included) || [];
          const groups = ((response.data && response.data.data) || []).map((item) => {
            const tags = included
              .filter((inc) => inc.type === "tag" && item.relationships && item.relationships.tags &&
                item.relationships.tags.data.some((r) => r.id === inc.id))
              .map((inc) => ({ id: parseInt(inc.id), ...inc.attributes }));
            return { id: parseInt(item.id), ...item.attributes, tags };
          });
          const items = [];
          groups.forEach((g) => {
            if (!g.tags.length) return;
            const key = "tags.system_" + g.name;
            items.push({ header: this.$te(key) ? this.$t(key) : g.name });
            g.tags.forEach((t) => items.push({ text: t.name, value: t.id, color: t.color }));
          });
          this.tagItems = items;
        })
        .catch((e) => console.log(e))
        .finally(() => { this.tagsLoading = false; });
    },
    // FT-087 REQ-09: пресеты фильтров в localStorage (per browser), guard малформленных
    loadPresets() {
      try {
        const arr = JSON.parse(localStorage.getItem(this.presetsKey) || "[]");
        if (!Array.isArray(arr)) return [];
        return arr.filter((p) => p && typeof p.name === "string" && p.filters);
      } catch (e) {
        return [];
      }
    },
    persistPresets() {
      try {
        localStorage.setItem(this.presetsKey, JSON.stringify(this.presets));
      } catch (e) {
        // storage недоступен/полон — держим в памяти, игнор
      }
    },
    savePreset() {
      const name = this.presetName.trim();
      if (!name) return;
      const f = this.local;
      const preset = {
        name,
        filters: {
          search: f.search || null,
          state: f.state || [],
          car_type: f.car_type || null,
          transmission: f.transmission || null,
          fuel: f.fuel || null,
          tag_ids: f.tag_ids || [],
          in_rent: f.in_rent === true ? true : null,
          active: f.active === true || f.active === false ? f.active : null,
        },
      };
      const idx = this.presets.findIndex((p) => p.name === name);
      if (idx >= 0) this.presets.splice(idx, 1, preset);
      else this.presets.push(preset);
      this.persistPresets();
      this.presetName = "";
    },
    applyPreset(p) {
      const f = p.filters || {};
      this.local = {
        ...this.local,
        search: f.search || null,
        state: f.state || [],
        car_type: f.car_type || null,
        transmission: f.transmission || null,
        fuel: f.fuel || null,
        tag_ids: f.tag_ids || [],
        in_rent: f.in_rent === true ? true : null,
        active: f.active === true || f.active === false ? f.active : null,
      };
      this.emitNow();
    },
    deletePreset(i) {
      this.presets.splice(i, 1);
      this.persistPresets();
    },
  },
};
</script>
